/**
 * Reverse-mode automatic differentiation over scalars.
 *
 * Every {@link Value} is a node in a dynamically-built computation graph (a
 * "tape"). Forward evaluation happens as you compose operations; calling
 * {@link Value.backward} walks the graph in reverse topological order and
 * accumulates the derivative of the root with respect to every node.
 *
 * The engine is deliberately small and dependency-free. It is fast enough to
 * differentiate an unrolled Newton solver for a planar linkage a few thousand
 * times per second in a browser worker, which is all the synthesizer needs.
 *
 * Design notes
 * ------------
 * - Each node stores its scalar `data`, its accumulated `grad`, the list of
 *   parents (`prev`), and a `backward` closure that pushes gradient onto those
 *   parents given the node's own `grad`.
 * - Gradients *accumulate* (`+=`), so a node used in several places is handled
 *   correctly. Call {@link Value.zeroGrad} (or build a fresh graph) between
 *   independent backward passes.
 * - Only the operations the kinematics/synthesis layers actually need are
 *   implemented, but that set is closed under everything those layers do.
 */

export type Num = Value | number;

let NODE_COUNTER = 0;

export class Value {
  data: number;
  grad: number;
  /** Parent nodes this value was computed from. */
  readonly prev: Value[];
  /** Human-readable tag for the producing operation (debugging only). */
  readonly op: string;
  /** Stable id, useful for stable topological ordering and debugging. */
  readonly id: number;
  /** Pushes this node's gradient onto its parents. No-op for leaves. */
  private backwardFn: () => void;

  constructor(data: number, prev: Value[] = [], op = "", backwardFn?: () => void) {
    if (typeof data !== "number" || Number.isNaN(data)) {
      throw new Error(`Value must wrap a real number, got ${data}`);
    }
    this.data = data;
    this.grad = 0;
    this.prev = prev;
    this.op = op;
    this.id = NODE_COUNTER++;
    this.backwardFn = backwardFn ?? (() => {});
  }

  // ---------------------------------------------------------------------------
  // Construction helpers
  // ---------------------------------------------------------------------------

  /** Wrap a plain number as a constant leaf (or pass a Value through). */
  static from(x: Num): Value {
    return x instanceof Value ? x : new Value(x, [], "const");
  }

  /** A named parameter leaf. Identical to `from`, but clearer at call sites. */
  static param(x: number, _name?: string): Value {
    return new Value(x, [], "param");
  }

  // ---------------------------------------------------------------------------
  // Binary operations
  // ---------------------------------------------------------------------------

  add(other: Num): Value {
    const o = Value.from(other);
    const out = new Value(this.data + o.data, [this, o], "+");
    out.backwardFn = () => {
      this.grad += out.grad;
      o.grad += out.grad;
    };
    return out;
  }

  sub(other: Num): Value {
    const o = Value.from(other);
    const out = new Value(this.data - o.data, [this, o], "-");
    out.backwardFn = () => {
      this.grad += out.grad;
      o.grad -= out.grad;
    };
    return out;
  }

  mul(other: Num): Value {
    const o = Value.from(other);
    const out = new Value(this.data * o.data, [this, o], "*");
    out.backwardFn = () => {
      this.grad += o.data * out.grad;
      o.grad += this.data * out.grad;
    };
    return out;
  }

  div(other: Num): Value {
    const o = Value.from(other);
    if (o.data === 0) throw new Error("Value.div: division by zero");
    const inv = 1 / o.data;
    const out = new Value(this.data * inv, [this, o], "/");
    out.backwardFn = () => {
      this.grad += inv * out.grad;
      o.grad += -this.data * inv * inv * out.grad;
    };
    return out;
  }

  /** `this ** exponent` for a *constant* real exponent. */
  pow(exponent: number): Value {
    const base = this.data;
    const out = new Value(Math.pow(base, exponent), [this], `**${exponent}`);
    out.backwardFn = () => {
      // d/dx x^k = k x^(k-1)
      this.grad += exponent * Math.pow(base, exponent - 1) * out.grad;
    };
    return out;
  }

  /** Two-argument arctangent, `atan2(this, x)`. Full-circle, sign-aware. */
  atan2(x: Num): Value {
    const xx = Value.from(x);
    const y = this.data;
    const denom = y * y + xx.data * xx.data;
    const out = new Value(Math.atan2(y, xx.data), [this, xx], "atan2");
    out.backwardFn = () => {
      if (denom === 0) return; // subgradient 0 at the origin
      this.grad += (xx.data / denom) * out.grad;
      xx.grad += (-y / denom) * out.grad;
    };
    return out;
  }

  /** Smooth minimum of `this` and `other` (constant not required). */
  min(other: Num): Value {
    const o = Value.from(other);
    const takeThis = this.data <= o.data;
    const out = new Value(takeThis ? this.data : o.data, [this, o], "min");
    out.backwardFn = () => {
      if (takeThis) this.grad += out.grad;
      else o.grad += out.grad;
    };
    return out;
  }

  max(other: Num): Value {
    const o = Value.from(other);
    const takeThis = this.data >= o.data;
    const out = new Value(takeThis ? this.data : o.data, [this, o], "max");
    out.backwardFn = () => {
      if (takeThis) this.grad += out.grad;
      else o.grad += out.grad;
    };
    return out;
  }

  // ---------------------------------------------------------------------------
  // Unary operations
  // ---------------------------------------------------------------------------

  neg(): Value {
    const out = new Value(-this.data, [this], "neg");
    out.backwardFn = () => {
      this.grad += -out.grad;
    };
    return out;
  }

  sin(): Value {
    const out = new Value(Math.sin(this.data), [this], "sin");
    const c = Math.cos(this.data);
    out.backwardFn = () => {
      this.grad += c * out.grad;
    };
    return out;
  }

  cos(): Value {
    const out = new Value(Math.cos(this.data), [this], "cos");
    const s = Math.sin(this.data);
    out.backwardFn = () => {
      this.grad += -s * out.grad;
    };
    return out;
  }

  tan(): Value {
    const t = Math.tan(this.data);
    const out = new Value(t, [this], "tan");
    out.backwardFn = () => {
      this.grad += (1 + t * t) * out.grad;
    };
    return out;
  }

  exp(): Value {
    const e = Math.exp(this.data);
    const out = new Value(e, [this], "exp");
    out.backwardFn = () => {
      this.grad += e * out.grad;
    };
    return out;
  }

  log(): Value {
    if (this.data <= 0) throw new Error("Value.log: argument must be positive");
    const out = new Value(Math.log(this.data), [this], "log");
    out.backwardFn = () => {
      this.grad += (1 / this.data) * out.grad;
    };
    return out;
  }

  sqrt(): Value {
    if (this.data < 0) throw new Error("Value.sqrt: argument must be non-negative");
    const r = Math.sqrt(this.data);
    const out = new Value(r, [this], "sqrt");
    out.backwardFn = () => {
      // guard the 1/(2*sqrt) singularity at 0
      this.grad += (r === 0 ? 0 : 0.5 / r) * out.grad;
    };
    return out;
  }

  abs(): Value {
    const s = this.data < 0 ? -1 : 1;
    const out = new Value(Math.abs(this.data), [this], "abs");
    out.backwardFn = () => {
      this.grad += s * out.grad;
    };
    return out;
  }

  /** Square, `this * this`. Cheaper and more stable than `pow(2)`. */
  square(): Value {
    const out = new Value(this.data * this.data, [this], "square");
    out.backwardFn = () => {
      this.grad += 2 * this.data * out.grad;
    };
    return out;
  }

  // ---------------------------------------------------------------------------
  // Reverse pass
  // ---------------------------------------------------------------------------

  /** Reset the gradient of this node and everything upstream of it to 0. */
  zeroGrad(): void {
    for (const node of this.topo()) node.grad = 0;
  }

  /**
   * Populate `grad` on every node upstream of this one with d(this)/d(node).
   * The seed derivative d(this)/d(this) defaults to 1.
   */
  backward(seed = 1): void {
    const order = this.topo();
    for (const node of order) node.grad = 0;
    this.grad = seed;
    for (let i = order.length - 1; i >= 0; i--) order[i].backwardFn();
  }

  /** Iterative post-order DFS: parents-before-children topological order. */
  private topo(): Value[] {
    const order: Value[] = [];
    const visited = new Set<number>();
    // stack of [node, childIndex]
    const stack: Array<[Value, number]> = [[this, 0]];
    visited.add(this.id);
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      const [node, idx] = frame;
      if (idx < node.prev.length) {
        frame[1]++;
        const child = node.prev[idx];
        if (!visited.has(child.id)) {
          visited.add(child.id);
          stack.push([child, 0]);
        }
      } else {
        order.push(node);
        stack.pop();
      }
    }
    return order;
  }
}

// ---------------------------------------------------------------------------
// Free-function sugar so kinematics code reads like math, not like Java.
// ---------------------------------------------------------------------------

export const V = (x: Num): Value => Value.from(x);
export const add = (a: Num, b: Num): Value => V(a).add(b);
export const sub = (a: Num, b: Num): Value => V(a).sub(b);
export const mul = (a: Num, b: Num): Value => V(a).mul(b);
export const div = (a: Num, b: Num): Value => V(a).div(b);
export const sin = (a: Num): Value => V(a).sin();
export const cos = (a: Num): Value => V(a).cos();
export const atan2 = (y: Num, x: Num): Value => V(y).atan2(x);
export const sqrt = (a: Num): Value => V(a).sqrt();
export const square = (a: Num): Value => V(a).square();

/** Sum a list of values/numbers, returning a Value. */
export function sum(items: Num[]): Value {
  let acc = V(0);
  for (const it of items) acc = acc.add(it);
  return acc;
}

/** Euclidean hypot of two components, sqrt(x^2 + y^2), numerically guarded. */
export function hypot(x: Num, y: Num): Value {
  return V(x).square().add(V(y).square()).add(1e-30).sqrt();
}
